import { useState } from 'react'
import { Modal, Field } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { AsyncMultiSelect } from '@/shared/ui/filters'
import { explain } from '@/shared/api/problem'
import { listUnits } from '@/features/geography/api'
import type { ProgramResponse } from '@/shared/api/types'
import { createProgram, updateProgram } from './api'
import { CATEGORY_ORDER, CHANNEL_ORDER, categoryLabel, channelLabel } from './vocab'
import { DatePicker } from '@/components/erp/date-picker'

/**
 * Create / edit a programme.
 *
 * ## Three fields are deliberately absent
 *
 * **`spentAmount`** is a trigger-maintained rollup of the disbursement ledger.
 * There is no input for it anywhere in this feature, because PollSphere had one
 * and its five money columns immediately disagreed.
 *
 * **`status`** moves only through the status action, which enforces the
 * transition table and refuses `DRAFT → ACTIVE` without a published rule
 * version. A dropdown here would offer transitions the server returns 409 for.
 *
 * **Milestones.** PollSphere sent the whole milestone array inside the
 * programme save, so every edit deleted and recreated every milestone and every
 * id changed — which means nothing could ever reference one. They have their own
 * endpoints and their own tab.
 *
 * ## `expectedUpliftPp` is here, and it is labelled
 *
 * It is a planning input. The label says "Planned", the help text says it is
 * never combined with a measured figure, and no impact screen uses it as an
 * operand. That sentence is the entire difference between this feature and the
 * product it replaces.
 */
export function ProgramFormDialog({ existing, onClose, onSaved }: {
  existing?: ProgramResponse
  onClose: () => void
  onSaved: (p: ProgramResponse) => void
}) {
  const editing = Boolean(existing)
  const [code, setCode] = useState(existing?.code ?? '')
  const [name, setName] = useState(existing?.name ?? '')
  const [description, setDescription] = useState(existing?.description ?? '')
  const [categoryCode, setCategoryCode] = useState(existing?.categoryCode ?? 'BURSARY')
  const [deliveryChannelCode, setChannel] = useState(existing?.deliveryChannelCode ?? 'MOBILE_MONEY')
  const [scopeUnits, setScopeUnits] = useState<{ value: string; label: string }[]>(
    existing?.scopeUnitId
      ? [{ value: existing.scopeUnitId, label: existing.scopeUnitPath ?? 'Selected unit' }]
      : [])
  const [startsOn, setStartsOn] = useState(existing?.startsOn ?? '')
  const [endsOn, setEndsOn] = useState(existing?.endsOn ?? '')
  const [currency, setCurrency] = useState(existing?.currency ?? 'KES')
  const [budgetAmount, setBudget] = useState(
    existing?.budgetAmount === null || existing?.budgetAmount === undefined
      ? '' : String(existing.budgetAmount))
  const [expectedUpliftPp, setUplift] = useState(
    existing?.expectedUpliftPp === null || existing?.expectedUpliftPp === undefined
      ? '' : String(existing.expectedUpliftPp))
  const [partnerName, setPartner] = useState(existing?.partnerName ?? '')

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setSaving(true); setError(null)
    const body = {
      ...(editing ? {} : { code }),
      name, description: description || null, categoryCode, deliveryChannelCode,
      scopeUnitId: scopeUnits[0]?.value ?? null,
      startsOn: startsOn || null, endsOn: endsOn || null,
      // An amount with no currency is not a quantity of anything, and the
      // server refuses the pair. Send both or neither.
      currency: budgetAmount ? currency : (currency || null),
      budgetAmount: budgetAmount === '' ? null : Number(budgetAmount),
      expectedUpliftPp: expectedUpliftPp === '' ? null : Number(expectedUpliftPp),
      partnerName: partnerName || null,
    }
    try {
      const saved = editing
        ? await updateProgram(existing!.id, body)
        : await createProgram(body)
      onSaved(saved)
    } catch (e) {
      setError(explain(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? `Edit ${existing!.name}` : 'New programme'}
      footer={(
        <>
          <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="erp-btn-primary" onClick={submit}
            disabled={saving || !name || (!editing && !code)}>
            {saving ? 'Saving…' : editing ? 'Save' : 'Create as draft'}
          </button>
        </>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />

        {!editing && (
          <Note tone="info">
            A new programme starts as a <strong>draft</strong>. It cannot be activated until a
            targeting rule version is published — until then its eligible pool is undefined and
            every coverage figure would divide by an unknown.
          </Note>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {!editing && (
            <Field label="Code" required>
              <input className="erp-input" value={code} onChange={(e) => setCode(e.target.value)}
                placeholder="PRG-016" maxLength={32} />
            </Field>
          )}
          <Field label="Name" required>
            <input className="erp-input" value={name} onChange={(e) => setName(e.target.value)}
              maxLength={200} />
          </Field>
          <Field label="Category">
            <select className="erp-input" value={categoryCode}
              onChange={(e) => setCategoryCode(e.target.value as typeof categoryCode)}>
              {CATEGORY_ORDER.map((c) => <option key={c} value={c}>{categoryLabel(c)}</option>)}
            </select>
          </Field>
          <Field label="Delivery channel">
            <select className="erp-input" value={deliveryChannelCode ?? ''}
              onChange={(e) => setChannel(e.target.value as typeof deliveryChannelCode)}>
              {CHANNEL_ORDER.map((c) => <option key={c} value={c}>{channelLabel(c)}</option>)}
            </select>
          </Field>
          <Field label="Starts on">
            <DatePicker value={startsOn ?? ''} onChange={setStartsOn} />
          </Field>
          <Field label="Ends on">
            <DatePicker value={endsOn ?? ''} onChange={setEndsOn} />
          </Field>
        </div>

        <Field
          label="Scope"
          hint="Leave empty for a national programme. A national programme is visible to every user, at every level."
        >
          <AsyncMultiSelect
            label="Scope unit"
            selected={scopeUnits}
            onChange={(next) => setScopeUnits(next.slice(-1))}
            search={async (term) => {
              const page = await listUnits({ q: term } as never, 0, 20)
              return page.content.map((u) => ({
                value: u.id, label: u.name || u.code || u.id, sub: u.path,
              }))
            }}
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Currency" hint="ISO-4217. One per programme.">
            <input className="erp-input" value={currency ?? ''} maxLength={3}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())} placeholder="KES" />
          </Field>
          <Field label="Budget" hint="Leave empty if there is no budget — the screens will say so rather than showing zero.">
            <input className="erp-input" type="number" min={0} value={budgetAmount}
              onChange={(e) => setBudget(e.target.value)} />
          </Field>
          <Field
            label="Planned uplift (pp)"
            hint="A PLAN, not a result. It is shown labelled ‘Planned’ and is never combined with a measured figure."
          >
            <input className="erp-input" type="number" min={-100} max={100} step="0.1"
              value={expectedUpliftPp} onChange={(e) => setUplift(e.target.value)} />
          </Field>
        </div>

        <Field label="Lead partner" hint="A display convenience. Full partner records live on the Partners tab.">
          <input className="erp-input" value={partnerName} maxLength={200}
            onChange={(e) => setPartner(e.target.value)} />
        </Field>

        <Field label="Description">
          <textarea className="erp-input min-h-[70px]" value={description ?? ''}
            onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}
